import { createReducer } from '@reduxjs/toolkit';
import { Cart, CartDelivery, CartStore } from '../';
import { DeliveryType } from '../types';

import {
  addCartItem,
  clearCart,
  clearCartStore,
  moveCartItem,
  removeCartItem,
  setCart,
  updateCartDelivery,
  updateCartItem,
} from '../actions/cart';
import {
  addToDelivery,
  createNewDelivery,
  findDelivery,
  initSumResults,
  populateCartItem,
  sumDelivery,
  sumResults,
} from '../cartHelpers';
import { selectDeliveryById } from '../selectors';

const initialState: CartStore = {};

export const newCart: Cart = {
  pricesForAllItems: true,
  deliveries: {},
  isValid: false,
  itemCount: 0,
};

export default createReducer(initialState, (builder) => {
  builder
    .addCase(addCartItem, (state, { payload }) => {
      const { cartKey } = payload;
      let deliveryId: string | null | undefined = payload.deliveryId;
      const cart = state[cartKey] ?? newCart;

      if (!deliveryId && !payload.delivery) {
        // try to find a delivery
        const defaultLocation =
          payload.locationId ?? payload.itemData.defaultLocation ?? 1;

        const deliveryType: DeliveryType =
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          (payload.itemData.quantityOptions?.default?.toUpperCase() as DeliveryType) ??
          DeliveryType.Delivery;
        deliveryId = findDelivery(cart, {
          locationId: defaultLocation,
          type: deliveryType,
          withoutId: payload.itemData.id,
        });

        if (
          deliveryId &&
          cart.deliveries[deliveryId].items[payload.itemData.id]
        ) {
          console.log('delivery already populated');
          deliveryId = null;
        }
      }
      let delivery: CartDelivery;

      if (!deliveryId) {
        delivery = createNewDelivery(payload);
      } else {
        delivery = addToDelivery(cart.deliveries[deliveryId], payload);
      }

      const deliveries = { ...cart.deliveries, [delivery.id]: delivery };

      const orderTotals = Object.values(deliveries)
        .map(sumDelivery)
        .reduce(sumResults, initSumResults);

      const deliveryTotal = Object.values(deliveries)
        .map((d) => ({ isValid: d.isValid }))
        .reduce((i, p) => ({ isValid: i.isValid && p.isValid }), {
          isValid: true,
        });

      const newItemCount = Object.values(deliveries).reduce(
        (count, delivery) => count + Object.keys(delivery.items).length,
        0,
      );

      return {
        ...state,
        [cartKey]: {
          ...cart,
          ...orderTotals,
          deliveries,
          ...deliveryTotal,
          itemCount: newItemCount,
        },
      };
    })
    .addCase(removeCartItem, (state, { payload }) => {
      const { cartKey, itemId, deliveryId } = payload;
      const currentDelivery = selectDeliveryById(
        cartKey,
        deliveryId,
      )({ cart: state });

      if (!currentDelivery) {
        console.error('no cart found');
        return state;
      }

      const items = { ...currentDelivery.items };
      delete items[itemId];

      let deliveries = { ...state[cartKey].deliveries };

      if (!Object.keys(items).length) {
        delete deliveries[deliveryId];
      } else {
        deliveries = {
          ...deliveries,
          [deliveryId]: {
            ...deliveries[deliveryId],
            items,
          },
        };
      }

      const orderTotals = Object.values(deliveries)
        .map(sumDelivery)
        .reduce(sumResults, initSumResults);

      const deliveryTotal = Object.values(deliveries)
        .map((d) => ({ isValid: d.isValid }))
        .reduce((i, p) => ({ isValid: i.isValid && p.isValid }), {
          isValid: true,
        });

      const newItemCount = Object.values(deliveries).reduce(
        (count, delivery) => count + Object.keys(delivery.items).length,
        0,
      );

      return {
        ...state,
        [cartKey]: {
          ...state[cartKey],
          deliveries,
          ...orderTotals,
          ...deliveryTotal,
          itemCount: newItemCount,
        },
      };
    })
    .addCase(updateCartItem, (state, { payload }) => {
      const { cartKey, deliveryId, itemId } = payload;

      const currentDelivery = selectDeliveryById(
        cartKey,
        deliveryId,
      )({ cart: state })!;

      const currentItem = currentDelivery.items[itemId];
      const items = {
        ...currentDelivery.items,
        [itemId]: populateCartItem(currentDelivery.items[itemId].itemData, {
          price: payload.price ?? currentItem.price ?? undefined,
          amount: payload.amount ?? currentItem.amount,
        }),
      };

      const deliveries = {
        ...state[cartKey].deliveries,
        [deliveryId]: {
          ...state[cartKey].deliveries[deliveryId],
          items,
        },
      };
      const orderTotals = Object.values(deliveries)
        .map(sumDelivery)
        .reduce(sumResults, initSumResults);

      return {
        ...state,
        [cartKey]: {
          ...state[cartKey],
          deliveries,
          ...orderTotals,
        },
      };
    })
    .addCase(moveCartItem, (state, { payload }) => {
      const { cartKey, itemId, deliveryId: currentDeliveryId } = payload;
      const deliveries = {
        ...state[cartKey].deliveries,
      };

      const currentDelivery = selectDeliveryById(
        cartKey,
        currentDeliveryId,
      )({ cart: state });

      if (!currentDelivery) {
        console.error('no cart found');
        return state;
      }

      const items = { ...currentDelivery.items };
      const oldItem = { ...items[itemId] };
      delete items[itemId];

      if (Object.keys(items).length === 0) {
        delete deliveries[currentDeliveryId];
      } else {
        deliveries[currentDeliveryId] = {
          ...deliveries[currentDeliveryId],
          items,
        };
      }

      let deliveryId = payload.newDeliveryId;
      let deliveryTotal;
      if (!deliveryId) {
        const del = createNewDelivery({
          itemData: oldItem.itemData,
          delivery: payload.newDelivery,
          locationId: currentDelivery.locationId,
          amount: oldItem.amount,
          price: oldItem.price || undefined,
        });

        deliveries[del.id] = del;
        deliveryId = del.id;

        deliveryTotal = Object.values(deliveries)
          .map((d) => ({ isValid: d.isValid }))
          .reduce((i, p) => ({ isValid: i.isValid && p.isValid }), {
            isValid: true,
          });
      } else {
        const delivery = { ...deliveries[deliveryId] };
        const validLocation = oldItem.itemData.locations.map((i) => i.id);
        if (!validLocation.includes(delivery.locationId)) {
          console.warn(
            `location ${delivery.locationId} not available for ${oldItem.itemData.id}`,
          );
          return state;
        }
        delivery.items = {
          ...delivery.items,
          [itemId]: oldItem,
        };

        deliveries[deliveryId] = delivery;
      }

      return {
        ...state,
        [cartKey]: {
          ...state[cartKey],
          deliveries,
          ...deliveryTotal,
        },
      };
    })
    .addCase(updateCartDelivery, (state, { payload }) => {
      const {
        deliveryId,
        cartKey,
        requiredDeliveryDate = false,
        ...changes
      } = payload;
      const d = selectDeliveryById(cartKey, deliveryId)({ cart: state });
      if (!d) {
        return state;
      }
      const delivery = { ...d };
      // Update simple fields
      if (changes.deliveryAddress !== undefined) {
        delivery.deliveryAddress = changes.deliveryAddress;
        delivery.isValid = true;
      }
      if (changes.deliveryFrom !== undefined) {
        delivery.deliveryFrom = changes.deliveryFrom;
        if (delivery.deliveryTo) {
          delivery.isValid = true;
        }
      }
      if (changes.deliveryTo !== undefined) {
        delivery.deliveryTo = changes.deliveryTo;
        if (delivery.deliveryFrom) {
          delivery.isValid = true;
        }
      }

      if (changes.deliveryTime !== undefined) {
        delivery.deliveryTime = changes.deliveryTime;
      }

      if (changes.comment !== undefined) {
        delivery.comment = changes.comment;
      }
      if (changes.siloId !== undefined) {
        delivery.siloId = changes.siloId;
      }

      if (changes.type !== undefined) {
        delivery.type = changes.type;
        if (changes.type === DeliveryType.Pickup && delivery.siloId) {
          delivery.siloId = null;
        }
      }

      // Update location, checking all the items are in that location
      if (changes.locationId !== undefined) {
        // TODO; check all items!
        delivery.locationId = changes.locationId;
      }

      // Check if the delivery is invalid due to siloId or requiredDeliveryDate without deliveryFrom and deliveryTo
      if (
        (delivery.siloId || requiredDeliveryDate) &&
        (!delivery.deliveryFrom || !delivery.deliveryTo)
      ) {
        delivery.isValid = false;
      }

      const deliveries = {
        ...state[cartKey].deliveries,
        [deliveryId]: delivery,
      };

      const deliveryTotal = Object.values(deliveries)
        .map((d) => ({ isValid: d.isValid }))
        .reduce((i, p) => ({ isValid: i.isValid && p.isValid }), {
          isValid: true,
        });

      return {
        ...state,
        [cartKey]: {
          ...state[cartKey],
          deliveries,
          ...deliveryTotal,
        },
      };
    })
    .addCase(setCart, (state, { payload }) => ({
      ...state,
      [payload.cartKey]: payload.cart,
    }))
    .addCase(clearCart.fulfilled, (state, { payload }) => {
      const { cartKey } = payload;
      const newState = { ...state };
      delete newState[cartKey];
      return newState;
    })

    .addCase(clearCartStore, () => initialState);
});
